/* globals $ */
import {
  BOOKS,
  SETS,
  LEVEL_TAG_PREFIX,
  INSTRUMENT_TAG_PREFIX
} from '_global/js/constants'

import { search } from 'libs/rcmSearchApi'

export default class Recommendations {
  constructor (params) {
    const defaults = {
      product: null,
      pageSize: 5,
      excludeIds: []
    }

    this.params = Object.assign(defaults, params)

    this.currentPage = 0
    this.recommendedItems = []
    this.excludeIds = []
    this.hasMore = true
    this.logicLayer = 0
    this.pageSize = this.params.pageSize
    this.isProcessing = false

    this.product = this.params.product

    this.currentResults = []

    this.getRecomendations = this.getRecomendations.bind(this)

    if (this.product && this.product.id) {
      this.excludeIds[this.product.id] = true
    }
    this.params.excludeIds.forEach((id) => {
      this.excludeIds[id] = true
    })
  }

  /**
   * Start the recommendation logic. Returns a promise once complete
   */
  getRecomendations () {
    this.currentResults = []

    return new Promise((resolve, reject) => {
      if (!this.hasMore) {
        this._returnResult(resolve)
        return
      }
      if (!this.product) {
        this._returnError(reject, 'No product defined.')
        return
      }
      if (this.isProcessing) {
        this._returnError(reject, 'Already processing, please wait and try again.')
        return
      }

      this.isProcessing = true

      switch (this.product.type) {
        case BOOKS:
        case SETS:
          this._getRecommendedBooks(resolve, reject)
          break

        default:
          this._getRecommendationsAPI(resolve, reject)
          break
      }
    })
  }

  /**
   * Adds the result set to the current and total results
   * @param {array} results - An array of product objects
   */
  _addResults (results, conformForm) {
    let filteredResults = []

    // process results
    results.forEach((product) => {
      // conform product object
      switch (conformForm) {
        case 'searchanise':
          product.id = product.product_id
          product.compare_at_price = product.list_price * 100
          product.price = product.price * 100
          product.featured_image = product.image_link.replace('_large', '')
          const splitLink = product.link.split('/')
          product.handle = splitLink[splitLink.length - 1]
          break
      }

      // make sure we don't already have this id
      if (!this.excludeIds[product.id]) {
        this.excludeIds[product.id] = true
        filteredResults.push(product)
      }
    })

    this.currentResults = this.currentResults.concat(filteredResults)
    this.recommendedItems = this.recommendedItems.concat(filteredResults)

    return filteredResults
  }

  /**
   * Resolves the promise return in a defined structure
   * @param {function} resolve - The promise resolve function
   */
  _returnResult (resolve) {
    this.isProcessing = false

    resolve({
      results: this.currentResults,
      allRecommendations: this.recommendedItems,
      hasMore: this.hasMore
    })
  }

  /**
   * Rejects the promise with an error
   * @param {function} reject - The promise reject function
   * @param {string} errorMessage - The error message
   */
  _returnError (reject, errorMessage) {
    this.isProcessing = false

    reject(new Error(errorMessage))
  }

  /**
   * Recursive function for fetching book recommendations
   * @param {function} resolve - The promise resolve function
   * @param {function} reject - The promise reject function
   */
  _getRecommendedBooks (resolve, reject) {
    let shouldSearch = false
    let searchParams = {
      facets: 'false',
      maxResults: 10,
      suggestions: 'false'
    }

    let levelTags = []
    let instrumentTags = []

    this.product.tags.forEach((tag) => {
      if (tag.indexOf(LEVEL_TAG_PREFIX) > -1) {
        levelTags.push(tag)
      } else if (tag.indexOf(INSTRUMENT_TAG_PREFIX) > -1) {
        instrumentTags.push(tag)
      }
    })

    // the different layers of recommendation logic
    while (shouldSearch === false && this.logicLayer <= 2) {
      switch (this.logicLayer) {
        case 0:
          // Find similar product type AND level AND instrument
          searchParams.product_type = this.product.type
          if (levelTags.length && instrumentTags.length) {
            searchParams.tags = levelTags.join('|') + ',' + instrumentTags.join('|')
            shouldSearch = true
          } else {
            this.logicLayer += 1
          }
          break

        case 1:
          // Find product type AND similar level
          searchParams.product_type = this.product.type
          if (levelTags.length) {
            searchParams.tags = levelTags.join('|')
            shouldSearch = true
          } else {
            this.logicLayer += 1
          }
          break

        case 2:
          // Find same product type
          searchParams.product_type = this.product.type
          searchParams.sortBy = 'sales_amount'
          searchParams.sortOrder = 'desc'
          shouldSearch = true
          break
      }
    }

    if (this.logicLayer > 2) {
      // If all else, get recommendations from Shopify API
      this._getRecommendationsAPI(resolve, reject)
      return
    }

    search.quickSearch('', searchParams)
      .then((data) => {
        if (data && data.items && data.items.length) {
          this._addResults(data.items, 'searchanise')
        }

        if (this.currentResults.length < this.pageSize) {
          // if we don't have enough recommendations to fill the minimum, increase the logic layer and go get more
          this.logicLayer += 1
          this._getRecommendedBooks(resolve, reject)
          return
        }

        this._returnResult(resolve)
      })
      .catch((jqXHR, textStatus, errorThrown) => {
        this._returnError(reject, textStatus)
      })
  }

  /**
   * Fetch Shopify Recommendations API recommendations
   * @param {function} resolve - The promise resolve function
   * @param {function} reject - The promise reject function
   */
  _getRecommendationsAPI (resolve, reject) {
    $.ajax({
      url: '/recommendations/products.json',
      method: 'GET',
      data: {
        product_id: this.product.id,
        limit: 10
      },
      dataType: 'json'
    })
      .done((data) => {
        this.hasMore = false

        if (data && data.products) {
          this._addResults(data.products)
        }

        this._returnResult(resolve)
      })
      .fail((jqXHR, textStatus, errorThrown) => {
        this._returnError(reject, textStatus)
      })
  }
}
